<template>
	<div class="flex flex-col gap-4 mb-10">
		<div class="border-b border-b-gray-lighter pb-6">
			<h1 class="mb-2.5 text-xl font-medium underline text-center text-gray-1">
				{{ currentCategory?.Name }}
			</h1>

			<div
				class="max-w-full md:max-w-[610px] md:mx-auto text-gray-900 text-center"
			>
				{{ stripHtmlTags(shortDescription) }}
			</div>
			<div
				v-if="currentCategory?.Description?.length > 300"
				class="text-center"
			>
				<a
					href="#"
					class="text-redcustom inline-block text-sm"
					@click.prevent="toggleShowMore"
				>
					{{
						isExpanded
							? "&nbsp; &uarr; " + $t("Show less")
							: "&darr; " + $t("Show more")
					}}
				</a>
			</div>
			<div
				v-if="subCategories?.length"
				class="mt-4 overflow-x-auto whitespace-nowrap pt-4 text-center"
			>
				<div class="inline-flex place-items-center gap-2 pb-4">
					<div
						v-for="subCategory in subCategories"
						:key="subCategory.CategoryId"
					>
						<Button
							:text="subCategory.Name"
							button-style="button-white text-primary border border-gray-300"
							button-height="h-12"
							:link="`/${createCategoryLink({
								category: subCategory
							})}`"
						/>
					</div>
				</div>
			</div>
			<div v-if="storyData?.top_blocks">
				<StoryblokComponent
					v-for="blok in storyData.top_blocks"
					:key="blok._uid"
					:blok="blok"
				/>
			</div>
			<ProductRecommendations
				v-if="currentCategory?.Level === 1 && recommendations"
				:blok="recommendations"
				:hierarchies="[[currentCategory.Name]]"
				title-styles="block text-lg text-center font-bold pb-5 border-b border-gray-300"
				class="mb-5 grid grid-cols-1"
			/>
		</div>
		<div v-if="!storyData?.hide_products">
			<div
				v-if="isSidebar"
				class="flex flex-wrap justify-end w-full text-primary gap-6"
			>
				<p class="text-sm font-medium mb-2">
					{{ filteredProducts?.ItemCount }} {{ $t("Products") }}
				</p>
				<div class="text-sm font-medium mb-2">
					<ElementsElSelect
						v-model="currentSortingOrder"
						name="sortingOrder"
						class="text-gray-700"
						:options="sortingOrdersOptions"
					/>
				</div>
			</div>
			<div class="flex flex-col lg:flex-row justify-between w-full">
				<Sidebar
					v-if="isSidebar"
					:bloks="categoryWidgetsRef"
					:filters="filters"
					:initial-selected-filters="selectedFilters.selectedFilters"
					@send-selected-filters="sendSelectedFilters"
				/>
				<ProductListing
					:filtered-products="filteredProducts"
					:page-size="pageSize"
					:picture-aspect-ratio="pictureAspectRatio"
					:page-no="pageNo"
					:is-loaded="isLoaded"
					:category-name="currentCategory?.Name"
					:row-view="storyData?.row_view"
					class="lg:pt-8"
					@get-next-page-of-products="pageNo++"
				/>
			</div>
		</div>
		<LazyLoadWrapper>
			<div v-if="filteredProducts?.ItemCount">
				<StoryblokComponent
					v-for="blok in preFooter"
					:key="blok._uid"
					:blok="blok"
				/>
			</div>
		</LazyLoadWrapper>

		<div v-if="storyData?.bottom_blocks">
			<StoryblokComponent
				v-for="blok in storyData.bottom_blocks"
				:key="blok._uid"
				:blok="blok"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import type { CreateFilterText } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import ProductListing from "./ProductListing.vue";
import Sidebar from "./Sidebar.vue";
import type { InitialData } from "~/types/components/category";
import type {
	BlokCategoryWidget,
	BlokColumns,
	BlokProductRecommendations,
	CategoryPageContent
} from "~/types/storyblok/storyblok";
import { getFiltersFromUrl } from "~/utils/categories/getFiltersFromUrl";
import { CategoryUrlParams } from "~/utils/constants";

interface Props {
	currentCategory: Category;
	initialData: InitialData;
	pageSize: string;
	pictureAspectRatio: string;
	categoryWidgets: BlokCategoryWidget[];
	initialCurrentSortingOrder: string;
	preFooter: BlokColumns[];
	storyData?: CategoryPageContent;
	recommendations?: BlokProductRecommendations;
}

const {
	currentCategory,
	initialData,
	pageSize,
	pictureAspectRatio,
	categoryWidgets,
	initialCurrentSortingOrder,
	preFooter
} = defineProps<Props>();

const { listProductByFilters, createFilterText, helloRetailApi } =
	useNorcePwa();
const norceStore = useNorceStore();
const { fetchAllParametrics } = useAllParametrics();
const {
	public: {
		norce: { isHelloRetailTracking }
	}
} = useRuntimeConfig();
const { createCategoryLink } = useCategory();

const { t } = useI18n();
const route = useRoute();

const sortingOrdersOptions = ref([
	{ label: t("Latest"), value: "SortOrder Desc" },
	{ label: t("Name A-Z"), value: "Name Asc" },
	{ label: t("Name Z-A"), value: "Name Desc" },
	{ label: t("Price ascending"), value: "Price Asc" },
	{ label: t("Price descending"), value: "Price Desc" }
]);
const initialDataRef = toRef(() => initialData);
const currentCategoryRef = toRef(() => currentCategory);
const categoryWidgetsRef = toRef(() => categoryWidgets);

const currentSortingOrder = ref(initialCurrentSortingOrder);
const subCategories = ref(currentCategoryRef.value?.Children);
const filteredProducts = ref(initialDataRef.value.products);
const filters = ref(initialDataRef.value.filters);
const isLoaded = ref<boolean>(true);

const selectedFilters = ref<CreateFilterText>(
	!route.query.filter
		? {
				allParametrics: [],
				selectedFilters: {}
			}
		: getFiltersFromUrl(route.query.filter as string)
);
const pageNo = ref(1);

const stripHtmlTags = (html: string) => {
	if (!html) return "";
	return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const sendSelectedFilters = async (
	ref: CreateFilterText,
	resetPageNo?: { resetPageNo: boolean }
) => {
	isLoaded.value = false;
	if (resetPageNo?.resetPageNo) {
		pageNo.value = 1;
	}

	ref.allParametrics = await fetchAllParametrics();
	const filterText = await createFilterText(ref);

	const params = {
		categorySeed: currentCategory?.CategoryId.toString(),
		filter: filterText,
		sort: currentSortingOrder.value,
		pageNo: pageNo.value.toString(),
		pageSize,
		statusSeed: "1",
		cultureCode: norceStore.cultureCode
	};

	const queryParams = Object.fromEntries(
		Object.entries(params)
			.filter(Boolean)
			.filter(
				([key, value]) =>
					value &&
					(key === CategoryUrlParams.FILTER ||
						(key === CategoryUrlParams.SORT && value !== "SortOrder Desc"))
			)
	);

	navigateTo({ query: queryParams });

	selectedFilters.value = ref;
	const data = await listProductByFilters(params);

	filteredProducts.value = data;

	isLoaded.value = true;
};

const isExpanded = ref(false);

const shortDescription = computed(() => {
	const description = currentCategory?.Description || "";
	if (isExpanded.value || description.length < 300) {
		return description;
	} else {
		return description.substring(0, 300) + "... ";
	}
});
const isSidebar = computed(
	() =>
		Object.keys(selectedFilters.value.selectedFilters).length > 0 ||
		filteredProducts.value?.ItemCount > 0
);

const toggleShowMore = () => {
	isExpanded.value = !isExpanded.value;
};

onMounted(() => {
	if (!isHelloRetailTracking) return;

	helloRetailApi.track({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			referrer: window.location.origin,
			url: window.location.href
		}
	});
});

watch(currentSortingOrder, async () => {
	await sendSelectedFilters(selectedFilters.value);
});

watch(pageNo, async () => {
	await sendSelectedFilters(selectedFilters.value);
});
</script>
